import { ImageOff } from 'lucide-react';
import { useState, forwardRef } from 'react';

import { cn } from '@/lib/utils';

import { Skeleton } from './skeleton';

interface ImageWithLoaderProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  skeletonClassName?: string;
  onLoad?: () => void;
  onError?: () => void;
  loading?: 'lazy' | 'eager';
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  fetchPriority?: 'high' | 'low' | 'auto';
  width?: number;
  height?: number;
  linkTo?: string;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLImageElement>) => void;
}

export const ImageWithLoader = forwardRef<HTMLImageElement, ImageWithLoaderProps>(
  (
    {
      src,
      alt,
      className,
      containerClassName,
      skeletonClassName,
      onLoad,
      onError,
      loading = 'lazy',
      onClick,
      fetchPriority,
      width,
      height,
      linkTo,
      draggable = true,
      onDragStart,
    },
    ref
  ) => {
    const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');

    const handleLoad = () => {
      setImageState('loaded');
      onLoad?.();
    };

    const handleError = () => {
      setImageState('error');
      onError?.();
    };

    return (
      <div className={cn('relative', containerClassName)} onClick={onClick}>
        {/* Loading skeleton */}
        {imageState === 'loading' && <Skeleton className={cn('', skeletonClassName)} />}

        {/* Image */}
        {linkTo ? (
          <a href={linkTo} target="_blank" rel="noopener noreferrer">
            <img
              ref={ref}
              src={src}
              alt={alt}
              className={cn(
                className,
                imageState === 'loaded' ? 'opacity-100' : 'opacity-0',
                'transition-opacity duration-300'
              )}
              onLoad={handleLoad}
              onError={handleError}
              loading={loading}
              fetchPriority={fetchPriority}
              width={width}
              height={height}
              draggable={draggable}
              onDragStart={onDragStart}
            />
          </a>
        ) : (
          <img
            ref={ref}
            src={src}
            alt={alt}
            className={cn(
              className,
              imageState === 'loaded' ? 'opacity-100' : 'opacity-0',
              'transition-opacity duration-300'
            )}
            onLoad={handleLoad}
            onError={handleError}
            loading={loading}
            fetchPriority={fetchPriority}
            width={width}
            height={height}
            draggable={draggable}
            onDragStart={onDragStart}
          />
        )}

        {/* Error message if loading fails */}
        {imageState === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-livelinx-grey100">
            <ImageOff className="mb-2 h-6 w-6 text-livelinx-grey200" />
            <p className="text-xs text-livelinx-grey200">Image unavailable</p>
          </div>
        )}
      </div>
    );
  }
);

ImageWithLoader.displayName = 'ImageWithLoader';
